import React, { createContext, useState, useContext, useEffect } from 'react';

// Create a context
const NotificationContext = createContext();

// Initial dummy notification data
const initialNotifications = [
  {
    id: 1,
    type: 'comment',
    user: 'Sarah Johnson',
    content: 'commented on your review: "Those chicken wings look amazing!"',
    timestamp: '2 hours ago',
    read: false,
    image: '/OIP.jpg'
  },
  {
    id: 2,
    type: 'comment',
    user: 'Marcus Lee',
    content: 'commented on your review: "I totally agree with your assessment!"',
    timestamp: '5 hours ago',
    read: false,
    image: '/OIP.jpg'
  },
  {
    id: 3,
    type: 'follow',
    user: 'Fariha Ahmed',
    content: 'started following you',
    timestamp: '1 day ago',
    read: true,
    image: '/OIP.jpg'
  },
  {
    id: 4,
    type: 'message',
    user: 'Lorenzo Parker',
    content: 'sent you a message: "Hey, want to try that new restaurant?"',
    timestamp: '2 days ago',
    read: true,
    image: '/OIP.jpg'
  },
  {
    id: 5,
    type: 'message',
    user: 'Haoyang Zhang',
    content: 'sent you a message about your recent review',
    timestamp: '3 days ago',
    read: true,
    image: '/OIP.jpg'
  }
];

// Provider component
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [authState, setAuthState] = useState({
    token: sessionStorage.getItem("token"),
    user: sessionStorage.getItem("user")
  });

  // Count unread notifications
  const unreadCount = notifications.filter(notification => !notification.read).length;

  // Function to load notifications
  const loadNotifications = () => {
    const isAuthenticated = sessionStorage.getItem("token") && sessionStorage.getItem("user");
    
    if (isAuthenticated) {
      setIsLoading(true);
      // Simulate API call delay
      const timer = setTimeout(() => {
        // Load the initialized state for demonstration purposes
        setNotifications(initialNotifications);
        setIsLoading(false);
      }, 1000);
      
      return timer;
    } else {
      // Clear notifications if not authenticated
      setNotifications([]);
      setIsLoading(false);
      return null;
    }
  };

  // Mark a notification as read
  const markAsRead = (id) => {
    setNotifications(prevNotifications => 
      prevNotifications.map(notification => 
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  };

  // Mark all notifications as read
  const markAllAsRead = () => {
    setNotifications(prevNotifications => 
      prevNotifications.map(notification => ({ ...notification, read: true }))
    );
  };

  // Add a new notification (for demonstration)
  const addNotification = (notification) => {
    setNotifications(prevNotifications => [
      {
        id: Date.now(), // Simple unique ID
        read: false,
        timestamp: 'Just now',
        ...notification
      },
      ...prevNotifications
    ]);
  };

  // Check for authentication changes
  useEffect(() => {
    // Function to check auth state
    const checkAuthState = () => {
      const token = sessionStorage.getItem("token");
      const user = sessionStorage.getItem("user");
      
      // If auth state changed
      if (token !== authState.token || user !== authState.user) {
        setAuthState({ token, user });
      }
    };
    
    // Check immediately
    checkAuthState();
    
    // Set up interval to check for auth changes
    const interval = setInterval(checkAuthState, 2000);
    
    return () => clearInterval(interval);
  }, [authState.token, authState.user]);

  // Load notifications when auth state changes
  useEffect(() => {
    const timer = loadNotifications();
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [authState]);

  return (
    <NotificationContext.Provider value={{ 
      notifications, 
      unreadCount, 
      isLoading,
      markAsRead, 
      markAllAsRead,
      addNotification
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook to use the notification context
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext; 